import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { db, auth } from './firebase';
import {
  collection, query, getDoc, getDocs, where, doc, 
  onSnapshot, addDoc, updateDoc, deleteDoc, increment, 
  orderBy, serverTimestamp, arrayUnion 
} from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import './CircleDetail.css';

// Audio Assets
import createSfx from './assets/create.mp3';
import editSfx from './assets/edit.mp3';
import deleteSfx from './assets/delete.mp3';
import enterSfx from './assets/enter.mp3';


export default function CircleDetail() {
  const { circleId } = useParams();
  /* Add a loading state for likes */
const [isLiking, setIsLiking] = useState(null); // stores itemId currently being liked

  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState('');
  const [friendEmail, setFriendEmail] = useState('');
  const [showInviteError, setShowInviteError] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
const [inviteMessage, setInviteMessage] = useState('');
  /* ==============================
     AUDIO
  ===============================*/
  const audioCreate = useRef(new Audio(createSfx));
  const audioEdit = useRef(new Audio(editSfx));
  const audioDelete = useRef(new Audio(deleteSfx));
  const audioEnter = useRef(new Audio(enterSfx));
  const playSound = (ref) => {
    ref.current.currentTime = 0;
    ref.current.play().catch(() => {});
  };

  /* ==============================
     REALTIME ITEMS LISTENER
  ===============================*/
  useEffect(() => {
    if (!auth.currentUser) return;

  audioEnter.current.currentTime = 0;
  audioEnter.current.play().catch(() => {});
    const q = query(
      collection(db, 'groups', circleId, 'items'),
      orderBy('likes', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => unsubscribe();
  }, [circleId]);

  /* ==============================
     POST ITEM
  ===============================*/
  /* Update handlePostItem */
const handlePostItem = async () => {
  if (!newItem.trim()) return;
  playSound(audioCreate);

  try {
    // 🌟 FORCE REFRESH: Get the latest profile data from Firebase Auth
    await auth.currentUser.reload(); 

    await addDoc(collection(db, 'groups', circleId, 'items'), {
      text: newItem,
      groupId: circleId,
      author: {
        uid: auth.currentUser.uid,
        // Now this will reflect the updated name
        name: auth.currentUser.displayName || 'Anonymous' 
      },
      likes: 0,
      likedBy: {}, 
      createdAt: serverTimestamp()
    });

    setNewItem('');
  } catch (error) {
    console.error("Error posting item:", error);
  }
};

  /* ==============================
     UPDATE ITEM
  ===============================*/
  /* Update handleUpdate */
const handleUpdate = async (itemId) => {
  if (!editText.trim()) return;
  playSound(audioEdit);

  const itemRef = doc(db, 'groups', circleId, 'items', itemId);
  
  // Rules check: only 'text' is allowed here for the author
  await updateDoc(itemRef, { 
    text: editText 
  });

  setEditingId(null);
};
  /* ==============================
     DELETE ITEM
  ===============================*/
  const handleDelete = async (itemId) => {
    playSound(audioDelete);
    await deleteDoc(doc(db, 'groups', circleId, 'items', itemId));
  };

  /* ==============================
     LIKE ITEM
  ===============================*/
  const handleLike = async (item) => {
  if (isLiking === item.id || item.likedBy?.[auth.currentUser.uid]) return;
  
  setIsLiking(item.id);
  try {
    await updateDoc(doc(db, 'groups', circleId, 'items', item.id), {
      likes: increment(1),
      [`likedBy.${auth.currentUser.uid}`]: true
    });
  } catch (err) {
    console.error("Like failed:", err);
  } finally {
    setIsLiking(null);
  }
};

const inviteFriend = async () => {
  if (!friendEmail.trim()) return;
  const email = friendEmail.trim().toLowerCase();

  if (email === auth.currentUser.email?.toLowerCase()) {
    setInviteMessage("You're already here");
    return;
  }

  try {
    const q = query(collection(db, 'users'), where('email', '==', email));
    const snap = await getDocs(q);

    if (snap.empty) {
      setInviteMessage("User not found in TeamFlow");
      setShowInviteError(true);
      return;
    }

    const friendDoc = snap.docs[0];
    const friendId = friendDoc.id;

    // Get current group data to check if they are already in the array
    const groupRef = doc(db, 'groups', circleId);
    const groupSnap = await getDoc(groupRef);
    const groupData = groupSnap.data();

    if (groupData.memberIds.includes(friendId)) {
      setInviteMessage("Already a member 👋");
      return;
    }

    // UPDATE THE ARRAYS (Firestore arrayUnion is best here)
    // You need to import { arrayUnion } from 'firebase/firestore';
    await updateDoc(groupRef, {
      memberIds: arrayUnion(friendId),
      memberEmails: arrayUnion(email)
    });

    setFriendEmail('');
    setInviteMessage("Member added! ✨");
  } catch (err) {
    console.error(err);
    setInviteMessage("Error adding member");
  }
};

  /* ==============================
     UI
  ===============================*/
  return (
    <div className="cd-page-root">

      <header className="cd-header">
        <div className="cd-invite-box">
          <input
            value={friendEmail}
           onChange={(e) => {
    setFriendEmail(e.target.value);
    if (showInviteError) setShowInviteError(false);
  }}
            
            placeholder="Invite by email..."
          />
          <button onClick={inviteFriend}>ADD</button>
        </div>
        {inviteMessage && (
  <div className="invite-info">
    {inviteMessage}
  </div>
)}
      </header>

      <div className="cd-main-input-area">
        <input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder="Add wish"
        />
        <button onClick={handlePostItem}>POST</button>
      </div>

      <div className="cd-posts-grid">
        <AnimatePresence>
          {items.map(item => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="cd-post-card"
            >
              {editingId === item.id ? (
                <>
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                  />
                  <div className="cd-admin-controls">
                    <button onClick={() => handleUpdate(item.id)}>SAVE</button>
                    <button onClick={() => setEditingId(null)}>CANCEL</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="cd-post-content">{item.text}</div>
                  {/* 🌟 ADD THIS LINE HERE */}
        <div className="item-author">by {item.author?.name || 'Anonymous'}</div>
                  <div className="cd-post-actions">
                    <button
  className="cd-like-sticker"
  onClick={() => handleLike(item)}
>
  ❤️ {item.likes || 0}
</button>


                    {item.author?.uid === auth.currentUser.uid && (
                      <div className="cd-admin-controls">
                        <button
                          onClick={() => {
                            setEditingId(item.id);
                            setEditText(item.text);
                          }}
                        >
                          EDIT
                        </button>
                        <button onClick={() => handleDelete(item.id)}>
                          DELETE
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

    </div>
  );
}
